/**
 * Workflow service for interacting with workflow API endpoints
 */
import api from './api';
import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';

/**
 * Parse a JWT token and extract the payload
 * @param {string} token - JWT token to parse
 * @returns {Object} Decoded token payload
 */
const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT token:', error);
    return null;
  }
};

// Updated to use the correct API endpoint structure prioritizing authenticated endpoints
const BASE_URL = '/api/workflow';

/**
 * Get all workflows
 * @returns {Promise<Array>} List of workflows
 */
export const getAllWorkflows = async () => {
  try {
    return await api.get(BASE_URL);
  } catch (error) {
    console.error('Error fetching workflows:', error);
    throw error;
  }
};

/**
 * Get a specific workflow by ID
 * @param {string} id - Workflow ID
 * @returns {Promise<Object>} Workflow data
 */
export const getWorkflowById = async (id) => {
  try {
    return await api.get(`${BASE_URL}/${id}`);
  } catch (error) {
    console.error(`Error fetching workflow ${id}:`, error);
    throw error;
  }
};

/**
 * Get workflow assignments for a specific workflow
 * @param {string} workflowId - Workflow ID
 * @returns {Promise<Array>} List of workflow assignments
 */
export const getWorkflowAssignments = async (workflowId) => {
  try {
    return await api.get(`${BASE_URL}/${workflowId}/assignments`);
  } catch (error) {
    console.error(`Error fetching assignments for workflow ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Get workflow assignments for the current customer organization
 * @returns {Promise<Array>} List of workflow assignments for the current organization
 */
export const getCustomerWorkflowAssignments = async () => {
  try {
    // We don't need to manually check for the token here since the API service will handle it
    console.log('Starting customer workflow assignments fetch');
    // Add additional debug info
    console.log('API base URL:', api.defaults?.baseURL || 'Not available');

    console.log('Fetching customer workflow assignments with auth token');
    
    // First try a direct test fetch to see if we can get real data
    try {
      console.log('Attempting direct test fetch first');
      const testResult = await testDirectFetchWithFetch();
      if (testResult && Array.isArray(testResult) && testResult.length > 0) {
        console.log('Direct test fetch succeeded with real data!');
        return testResult;
      } else {
        console.log('Direct test fetch returned empty or invalid data, trying standard endpoints');
      }
    } catch (testError) {
      console.warn('Direct test fetch failed:', testError.message);
    }
    
    // This endpoint returns all workflow assignments for the authenticated user's organization
    let response;
    try {
      console.log('Attempting to fetch from primary endpoint: /api/workflow-assignments/customer');
      response = await api.get('/api/workflow-assignments/customer');
      console.log('Primary endpoint succeeded');
    } catch (firstError) {
      console.warn('Primary endpoint failed:', firstError.message);
      try {
        // Try the alternative endpoint
        console.log('Attempting to fetch from alternative endpoint: /v1/workflow-assignments/customer');
        response = await api.get('/v1/workflow-assignments/customer');
        console.log('Alternative endpoint succeeded');
      } catch (secondError) {
        console.error('Both endpoints failed:', secondError.message);
        throw secondError;
      }
    }
    
    // Log response status and details
    if (response) {
      console.log('API Response status:', response.status);
      console.log('API Response headers:', response.headers);
      console.log('Customer workflow assignments response:', response);
      
      // Detailed response analysis
      if (response.data) {
        console.log('Response contains data property');
        console.log('Response.data type:', typeof response.data);
        console.log('Is response.data an array:', Array.isArray(response.data));
        if (Array.isArray(response.data)) {
          console.log(`Response.data contains ${response.data.length} items`);
          // Log the first item structure if available
          if (response.data.length > 0) {
            console.log('First item structure:', JSON.stringify(response.data[0], null, 2));
          }
        }
      }
    }
    
    // Process and return the data
    if (response && response.data && Array.isArray(response.data)) {
      // Standard Axios response with data property
      console.log(`Found ${response.data.length} workflow assignments in response.data`);
      
      // Log workflow names for debugging
      console.log('\n=== WORKFLOW NAMES FROM RESPONSE.DATA ===');
      response.data.forEach((item, index) => {
        if (item && item.workflow && item.workflow.name) {
          console.log(`${index + 1}. ${item.workflow.name}`);
        } else {
          console.log(`${index + 1}. Item structure:`, item);
        }
      });
      console.log('=====================================\n');
      
      return response.data;
    } else if (Array.isArray(response)) {
      // Direct array response (uncommon but possible)
      console.log(`Found ${response.length} workflow assignments in direct response`);
      return response;
    } else {
      console.warn('No valid workflow assignments data found in response');
      console.warn('Response structure:', response);
      return [];
    }
  } catch (error) {
    console.error('Error fetching customer workflow assignments:', error);
    // Return empty array instead of throwing to prevent component errors
    return [];
  }
};

/**
 * Get workflow assignments for a specific organization
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Array>} List of workflow assignments for the organization
 */
export const getOrganizationWorkflowAssignments = async (organizationId) => {
  try {
    if (!organizationId) {
      console.warn('Organization ID is required');
      return [];
    }
    
    // Add additional debug info
    console.log('API base URL:', api.defaults?.baseURL || 'Not available');
    console.log('Using real data for workflow assignments');
    console.log('Current time:', new Date().toISOString());
    
    console.log(`Fetching workflow assignments for organization: ${organizationId}`);
    console.log(`API URL: /api/workflow-assignments/organization/${organizationId}`);
    
    // Make the API call
    const response = await api.get(`/api/workflow-assignments/organization/${organizationId}`);
    
    // Detailed logging of the response
    console.log(`Organization workflow assignments for ${organizationId}:`, response);
    
    // Detailed response analysis
    if (response) {
      console.log('API Response status:', response.status);
      console.log('API Response headers:', response.headers);
      
      if (response.data) {
        console.log('Response contains data property');
        console.log('Response.data type:', typeof response.data);
        console.log('Is response.data an array:', Array.isArray(response.data));
        if (Array.isArray(response.data)) {
          console.log(`Response.data contains ${response.data.length} items`);
          // Log the first item structure if available
          if (response.data.length > 0) {
            console.log('First item structure:', JSON.stringify(response.data[0], null, 2));
          }
        }
      }
    }
    
    // Process and return the data
    if (response && response.data && Array.isArray(response.data)) {
      // Standard Axios response with data property
      console.log(`Found ${response.data.length} organization workflow assignments in response.data`);
      
      // Log workflow names for debugging
      console.log('\n=== WORKFLOW NAMES FROM RESPONSE.DATA ===');
      response.data.forEach((item, index) => {
        if (item && item.workflow && item.workflow.name) {
          console.log(`${index + 1}. ${item.workflow.name}`);
        } else {
          console.log(`${index + 1}. Item structure:`, item);
        }
      });
      console.log('=====================================\n');
      
      // Return the actual data from the API
      return response.data;
    } else if (Array.isArray(response)) {
      // Direct array response (uncommon but possible)
      console.log(`Found ${response.length} organization workflow assignments in direct response`);
      
      // Log workflow names for debugging
      console.log('\n=== WORKFLOW NAMES FROM DIRECT RESPONSE ===');
      response.forEach((item, index) => {
        if (item && item.workflow && item.workflow.name) {
          console.log(`${index + 1}. ${item.workflow.name}`);
        } else {
          console.log(`${index + 1}. Item structure:`, item);
        }
      });
      console.log('=====================================\n');
      
      return response;
    } else {
      console.warn('No valid workflow assignments data found in response');
      console.warn('Response structure:', response);
      return [];
    }
  } catch (error) {
    console.error('Error fetching organization workflow assignments:', error);
    return [];
  }
};

/**
 * Get all customer organizations
 * @returns {Promise<Array>} List of customer organizations
 */
export const getCustomerOrganizations = async () => {
  try {
    return await api.get(`${BASE_URL}/customer-organisations`);
  } catch (error) {
    console.error('Error fetching customer organizations:', error);
    throw error;
  }
};

/**
 * Assign a workflow to an organization
 * @param {string} workflowId - Workflow ID
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Created workflow assignment
 */
export const assignWorkflow = async (workflowId, organizationId) => {
  try {
    return await api.post(`${BASE_URL}/${workflowId}/assign/${organizationId}`);
  } catch (error) {
    console.error(`Error assigning workflow ${workflowId} to organization ${organizationId}:`, error);
    throw error;
  }
};

/**
 * Deactivate a workflow assignment
 * @param {string} assignmentId - Assignment ID
 * @returns {Promise<Object>} Updated workflow assignment
 */
export const deactivateAssignment = async (assignmentId) => {
  try {
    return await api.put(`${BASE_URL}/assignments/${assignmentId}/deactivate`);
  } catch (error) {
    console.error(`Error deactivating assignment ${assignmentId}:`, error);
    throw error;
  }
};

/**
 * Create a new workflow
 * @param {Object} workflowData - Workflow data
 * @returns {Promise<Object>} Created workflow
 */
export const createWorkflow = async (workflowData) => {
  try {
    return await api.post(BASE_URL, workflowData);
  } catch (error) {
    console.error('Error creating workflow:', error);
    throw error;
  }
};

/**
 * Update an existing workflow
 * @param {string} id - Workflow ID
 * @param {Object} workflowData - Updated workflow data
 * @returns {Promise<Object>} Updated workflow
 */
export const updateWorkflow = async (id, workflowData) => {
  try {
    return await api.put(`${BASE_URL}/${id}`, workflowData);
  } catch (error) {
    console.error(`Error updating workflow ${id}:`, error);
    throw error;
  }
};

/**
 * Test function to directly fetch workflow assignments using axios
 * This bypasses the API service to test if the issue is with the service or the API
 * @returns {Promise<Array>} List of workflow assignments
 */
export const testDirectFetch = async () => {
  try {
    // Import axios directly
    const axios = require('axios');
    
    // Get the token from localStorage
    const jwtToken = localStorage.getItem('jwt_token');
    const authInfoStr = localStorage.getItem('authInfo');
    
    // Log token information
    console.log('\n=== DIRECT FETCH TEST ===');
    console.log('JWT Token available:', !!jwtToken);
    console.log('AuthInfo available:', !!authInfoStr);
    
    // Try to extract token from authInfo if jwt_token is not available
    let token = jwtToken;
    if (!token && authInfoStr) {
      try {
        const authInfo = JSON.parse(authInfoStr);
        token = authInfo.token;
        console.log('Token extracted from authInfo');
      } catch (e) {
        console.error('Error parsing authInfo:', e);
      }
    }
    
    if (!token) {
      console.error('No token available for direct fetch');
      return [];
    }
    
    // Log the token being used
    console.log('Using token:', token.substring(0, 20) + '...');
    
    // Make direct request to the API
    const response = await axios.get(`${API_BASE_URL}/api/workflow-assignments/customer`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    console.log('Direct fetch response:', response);
    
    if (response && response.data) {
      console.log('Direct fetch data:', response.data);
      return Array.isArray(response.data) ? response.data : [];
    }
    
    return [];
  } catch (error) {
    console.error('Error in direct fetch:', error);
    return [];
  }
};

/**
 * Test function to directly fetch workflow assignments using the Fetch API
 * This provides an alternative to axios to test if the issue is with axios or the API
 * @returns {Promise<Array>} List of workflow assignments
 */
export const testDirectFetchWithFetch = async () => {
  try {
    console.log('Attempting direct fetch with Fetch API');
    
    // Get the token from localStorage
    const token = localStorage.getItem('jwt_token') || 
                 (localStorage.getItem('authInfo') ? 
                  JSON.parse(localStorage.getItem('authInfo')).token : null);
    
    if (!token) {
      console.warn('No authentication token available for direct fetch');
      return [];
    }
    
    console.log('Using token for direct fetch');
    
    // Try to fetch from the organization endpoint using the organization ID
    // First check if organizationId is directly stored in localStorage
    let orgId = localStorage.getItem('organizationId');
    
    // If not found in direct localStorage, try other methods
    if (!orgId) {
      try {
        // Try to decode the JWT token to get the principal info
        const tokenPayload = parseJwt(token);
        if (tokenPayload && tokenPayload.org) {
          // Use org from payload if available
          orgId = tokenPayload.org;
        } else {
          // Fallback: Try to get from authInfo in localStorage
          const userInfo = JSON.parse(localStorage.getItem('authInfo') || '{}');
          if (userInfo && userInfo.principalInfo && userInfo.principalInfo.organisationId) {
            orgId = userInfo.principalInfo.organisationId;
          } else {
            console.error('Could not find organization ID in localStorage, token, or authInfo');
            return [];
          }
        }
      } catch (error) {
        console.error('Error finding organization ID:', error);
        return [];
      }
    }
    
    console.log(`Using organization ID from token: ${orgId}`);
    const url = `${API_BASE_URL}/api/workflow-assignments/organization/${orgId}`;
    
    console.log(`Making direct fetch request to: ${url}`);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Direct fetch response:', data);
    
    if (Array.isArray(data)) {
      console.log(`Direct fetch found ${data.length} workflow assignments`);
      // Log workflow names for debugging
      data.forEach((item, index) => {
        if (item && item.workflow && item.workflow.name) {
          console.log(`${index + 1}. ${item.workflow.name}`);
        }
      });
      return data;
    } else {
      console.warn('Direct fetch response is not an array:', data);
      return [];
    }
  } catch (error) {
    console.error('Error in direct fetch:', error);
    return [];
  }
};

/**
 * Get fields for a specific workflow
 * @param {string} workflowId - Workflow ID
 * @returns {Promise<Object>} Object containing customer info fields and form fields
 */
export const getWorkflowFields = async (workflowId) => {
  try {
    console.log(`Fetching fields for workflow ID: ${workflowId}`);
    
    // Try multiple approaches to get fields
    let fieldsData = null;
    
    console.log('Trying to get fields using the workflow definition');
    try {
      // Get the workflow definition to extract steps
      const workflow = await getWorkflowById(workflowId);
      console.log('Workflow API response:', workflow);
      
      if (workflow && workflow.definition) {
        console.log('Workflow retrieved successfully');
        
        // Parse the definition if it's a string
        let definition = workflow.definition;
        if (typeof definition === 'string') {
          try {
            definition = JSON.parse(definition);
            console.log('Successfully parsed workflow definition');
          } catch (parseError) {
            console.error('Error parsing workflow definition:', parseError);
          }
        }
        
        console.log('Full workflow definition:', definition);
        
        // Extract steps from the definition
        if (definition.steps && Array.isArray(definition.steps)) {
          console.log(`Found ${definition.steps.length} steps in workflow definition`);
          console.log('Steps from definition:', definition.steps);
          
          // Check if any step has staticFields
          const hasStaticFields = definition.steps.some(step => 
            step.staticFields && Array.isArray(step.staticFields) && step.staticFields.length > 0
          );
          
          if (hasStaticFields) {
            console.log('Found static fields in workflow definition, using them directly');
          } else {
            console.log('No static fields found in steps, adding them if available in definition root');
            // If the workflow has staticFields at the root level, add them to the first step
            if (definition.staticFields && Array.isArray(definition.staticFields) && definition.staticFields.length > 0) {
              definition.steps.forEach(step => {
                if (!step.staticFields) {
                  step.staticFields = definition.staticFields;
                  console.log(`Added ${definition.staticFields.length} static fields to step ${step.id}`);
                }
              });
            }
          }
          
          // Use the updated function to get fields for these steps
          fieldsData = await getFieldsForWorkflowSteps(definition.steps);
          console.log('Successfully retrieved fields from workflow definition');
        } else {
          console.log('No steps found in workflow definition');
        }
      } else {
        console.error('No workflow or definition found in API response');
      }
    } catch (serviceFieldsError) {
      console.error('Error getting fields from workflow definition:', serviceFieldsError);
    }
    
    // If no fields were found from the workflow definition, log the error
    if (!fieldsData || (!fieldsData.customerInfoFields.length && !fieldsData.formFields.length)) {
      console.log('No fields found in workflow definition, the UI might not display proper fields');
      const workflow = await getWorkflowById(workflowId);
      
      if (workflow && workflow.definition) {
        let definition = workflow.definition;
        if (typeof definition === 'string') {
          try {
            definition = JSON.parse(definition);
          } catch (parseError) {
            console.error('Error parsing workflow definition:', parseError);
            throw parseError;
          }
        }

        if (definition.steps && Array.isArray(definition.steps)) {
          fieldsData = {
            customerInfoFields: [],
            formFields: []
          };

          // Extract fields from each step
          definition.steps.forEach(step => {
            if (step.formFields && Array.isArray(step.formFields)) {
              step.formFields.forEach(field => {
                const formattedField = {
                  id: field.code || field.title.toLowerCase().replace(/\s+/g, '_'),
                  name: field.title,
                  label: field.title,
                  type: field.type.toLowerCase(),
                  required: field.mandatory || false,
                  category: field.category || 'GENERAL',
                  subCategory: field.subCategory,
                  isStatic: field.isStatic || false,
                  options: field.options || null
                };

                fieldsData.formFields.push(formattedField);
              });
            }
          });
        }
      }
    }
    
    // If both approaches failed, try to extract fields from the workflow definition
    if (!fieldsData || (!fieldsData.customerInfoFields.length && !fieldsData.formFields.length)) {
      console.log('Both approaches failed, trying to extract fields directly from workflow definition');
      try {
        // Get the workflow definition again if we don't already have it
        let workflow = null;
        let definition = null;
        
        try {
          workflow = await getWorkflowById(workflowId);
          if (workflow && workflow.definition) {
            definition = workflow.definition;
            if (typeof definition === 'string') {
              definition = JSON.parse(definition);
            }
          }
        } catch (error) {
          console.error('Error getting workflow definition:', error);
        }
        
        if (definition) {
          // Extract fields from the definition
          const customerInfoFields = [];
          const formFields = [];
          
          // Check if definition has fields property
          if (definition.fields) {
            // If fields are already categorized
            if (definition.fields.customerInfoFields) {
              customerInfoFields.push(...definition.fields.customerInfoFields);
            }
            if (definition.fields.formFields) {
              formFields.push(...definition.fields.formFields);
            }
            // If fields are in a flat array, categorize them
            if (Array.isArray(definition.fields)) {
              definition.fields.forEach(field => {
                if (field.category === 'CUSTOMER_INFO') {
                  customerInfoFields.push(field);
                } else {
                  formFields.push(field);
                }
              });
            }
          }
          
          // Check if definition has steps with fields
          if (definition.steps && Array.isArray(definition.steps)) {
            definition.steps.forEach(step => {
              if (step.fields && Array.isArray(step.fields)) {
                step.fields.forEach(field => {
                  if (field.category === 'CUSTOMER_INFO') {
                    customerInfoFields.push(field);
                  } else {
                    formFields.push(field);
                  }
                });
              } else if (step.serviceName) {
                // Generate a default field for this step if it has a service
                const stepField = {
                  id: `field-${step.id || step.name.toLowerCase().replace(/\s+/g, '-')}`,
                  name: `${step.name} Details`,
                  label: `${step.name} Details`,
                  type: 'textarea',
                  required: step.required || false,
                  category: 'SERVICE',
                  serviceId: step.serviceId,
                  serviceName: step.serviceName,
                  order: formFields.length + 1
                };
                formFields.push(stepField);
              }
            });
          }
          
          fieldsData = { customerInfoFields, formFields };
        }
      } catch (extractError) {
        console.error('Error extracting fields from workflow definition:', extractError);
      }
    }
    
    // If we have fields data, return it
    if (fieldsData && (fieldsData.customerInfoFields.length > 0 || fieldsData.formFields.length > 0)) {
      return fieldsData;
    }
    
    // If all approaches failed, return default fields
    console.warn('All approaches to get workflow fields failed, returning default fields');
    return { 
      customerInfoFields: [
        {
          id: 'customer-name',
          name: 'Customer Name',
          label: 'Customer Name',
          type: 'text',
          required: true,
          category: 'CUSTOMER_INFO',
          order: 1
        },
        {
          id: 'customer-email',
          name: 'Customer Email',
          label: 'Customer Email',
          type: 'email',
          required: true,
          category: 'CUSTOMER_INFO',
          order: 2
        },
        {
          id: 'customer-phone',
          name: 'Customer Phone',
          label: 'Customer Phone',
          type: 'tel',
          required: true,
          category: 'CUSTOMER_INFO',
          order: 3
        }
      ], 
      formFields: [] 
    };
  } catch (error) {
    console.error(`Error in getWorkflowFields: ${error.message}`);
    
    // If there's an error, return default fields
    return { 
      customerInfoFields: [
        {
          id: 'customer-name',
          name: 'Customer Name',
          label: 'Customer Name',
          type: 'text',
          required: true,
          category: 'CUSTOMER_INFO',
          order: 1
        },
        {
          id: 'customer-email',
          name: 'Customer Email',
          label: 'Customer Email',
          type: 'email',
          required: true,
          category: 'CUSTOMER_INFO',
          order: 2
        },
        {
          id: 'customer-phone',
          name: 'Customer Phone',
          label: 'Customer Phone',
          type: 'tel',
          required: true,
          category: 'CUSTOMER_INFO',
          order: 3
        }
      ], 
      formFields: [] 
    };
  }
};

/**
 * Get all services with their fields
 * @returns {Promise<Array>} List of services with their fields
 */
export const getAllServicesWithFields = async () => {
  try {
    console.log('Fetching all services with their fields');
    
    const response = await api.get('/v1/service/fields');
    console.log('Services with fields response:', response);
    
    // Always process the response, even if it doesn't match the expected format exactly
    if (response && response.data) {
      const servicesData = Array.isArray(response.data) ? response.data : [response.data];
      
      // Process the response to ensure fields are in the expected format
      const processedServices = servicesData.map(service => {
        // Process dynamic fields if they exist
        const processedFields = Array.isArray(service.fields) ? 
          service.fields.map(field => ({
            ...field,
            // Ensure these properties exist with default values if they don't
            id: field.id || `field-${Math.random().toString(36).substr(2, 9)}`,
            title: field.title || 'Untitled Field',
            type: field.type || 'TEXT',
            mandatory: field.mandatory || false,
            category: field.category || 'FORM'
          })) : [];
        
        // Process static fields if they exist (convert to field objects)
        const staticFieldObjects = Array.isArray(service.staticFields) ?
          service.staticFields.map(fieldCode => {
            // Use the mapStaticFieldCodeToField function to convert codes to objects
            const fieldObj = mapStaticFieldCodeToField(fieldCode);
            return {
              id: fieldObj.id,
              title: fieldObj.label,
              name: fieldObj.name,
              type: fieldObj.type,
              mandatory: fieldObj.required,
              category: 'CUSTOMER_INFO',
              fieldCode: fieldCode
            };
          }) : [];
        
        // Combine dynamic and static fields
        const allFields = [...processedFields, ...staticFieldObjects];
        
        return {
          ...service,
          fields: allFields
        };
      });
      
      console.log('Processed services with fields:', processedServices);
      return processedServices;
    }
    
    // If we get here, there was no data in the response
    console.log('No services data found in response, returning empty array');
    return [];
  } catch (error) {
    console.error(`Error in getAllServicesWithFields: ${error.message}`);
    return [];
  }
};

/**
 * Map static field codes to field objects
 * @param {string} fieldCode - Static field code from the API
 * @returns {Object} Field object with id, name, label, type, and required properties
 */
const mapStaticFieldCodeToField = (fieldCode) => {
  console.log(`Mapping static field code: ${fieldCode}`);
  
  // Define mappings for known field codes - comprehensive list based on workflow definition
  const fieldMappings = {
    // Name fields
    'firstNameEn': {
      id: 'first-name-en',
      name: 'First Name (English)',
      label: 'First Name (English)',
      type: 'TEXT',  // Changed to match the type in API response
      required: true,
      category: 'CUSTOMER_INFO',
      order: 1
    },
    'lastNameEn': {
      id: 'last-name-en',
      name: 'Last Name (English)',
      label: 'Last Name (English)',
      type: 'TEXT',
      required: true,
      category: 'CUSTOMER_INFO',
      order: 2
    },
    'firstName': {
      id: 'first-name',
      name: 'First Name',
      label: 'First Name',
      type: 'TEXT',
      required: true,
      category: 'CUSTOMER_INFO',
      order: 3
    },
    'lastName': {
      id: 'last-name',
      name: 'Last Name',
      label: 'Last Name',
      type: 'TEXT',
      required: true,
      category: 'CUSTOMER_INFO',
      order: 4
    },
    'firstNameAr': {
      id: 'first-name-ar',
      name: 'First Name (Arabic)',
      label: 'First Name (Arabic)',
      type: 'TEXT',
      required: false,
      category: 'CUSTOMER_INFO',
      order: 5
    },
    'lastNameAr': {
      id: 'last-name-ar',
      name: 'Last Name (Arabic)',
      label: 'Last Name (Arabic)',
      type: 'TEXT',
      required: false,
      category: 'CUSTOMER_INFO',
      order: 6
    },
    
    // Contact and address fields
    'email': {
      id: 'email',
      name: 'Email',
      label: 'Email Address',
      type: 'TEXT',  // Changed to match the type in API response
      required: true,
      category: 'CUSTOMER_INFO',
      order: 7
    },
    'mobile': {
      id: 'mobile',
      name: 'Mobile Number',
      label: 'Mobile Number',
      type: 'TEXT',
      required: true,
      category: 'CUSTOMER_INFO',
      order: 8
    },
    'phone': {
      id: 'phone',
      name: 'Phone Number',
      label: 'Phone Number',
      type: 'TEXT',
      required: false,
      category: 'CUSTOMER_INFO',
      order: 9
    },
    'address': {
      id: 'address',
      name: 'Address',
      label: 'Address',
      type: 'TEXT',
      required: true,
      category: 'CUSTOMER_INFO',
      order: 10
    },
    'area': {
      id: 'area',
      name: 'Area',
      label: 'Area',
      type: 'TEXT',
      required: false,
      category: 'CUSTOMER_INFO',
      order: 11
    },
    'buildingNumber': {
      id: 'building-number',
      name: 'Building Number',
      label: 'Building Number',
      type: 'TEXT',
      required: false,
      category: 'CUSTOMER_INFO',
      order: 12
    },
    
    // Personal information fields
    'dob': {
      id: 'date-of-birth',
      name: 'Date of Birth',
      label: 'Date of Birth',
      type: 'DATE',
      required: true,
      category: 'CUSTOMER_INFO',
      order: 13
    },
    'gender': {
      id: 'gender',
      name: 'Gender',
      label: 'Gender',
      type: 'TEXT',  // Changed to match the type in API response
      options: [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'other', label: 'Other' }
      ],
      required: true,
      category: 'CUSTOMER_INFO',
      order: 14
    },
    'nationality': {
      id: 'nationality',
      name: 'Nationality',
      label: 'Nationality',
      type: 'TEXT',
      required: false,
      category: 'CUSTOMER_INFO',
      order: 15
    },
    
    // Document fields
    'passportNumber': {
      id: 'passport-number',
      name: 'Passport Number',
      label: 'Passport Number',
      type: 'TEXT',
      required: false,
      category: 'CUSTOMER_INFO',
      order: 16
    },
    'passportExpiry': {
      id: 'passport-expiry',
      name: 'Passport Expiry',
      label: 'Passport Expiry',
      type: 'DATE',
      required: false,
      category: 'CUSTOMER_INFO',
      order: 17
    },
    'emirateId': {
      id: 'emirate-id',
      name: 'Emirates ID',
      label: 'Emirates ID',
      type: 'TEXT',
      required: false,
      category: 'CUSTOMER_INFO',
      order: 18
    },
    'emirateIdExpiry': {
      id: 'emirate-id-expiry',
      name: 'Emirates ID Expiry',
      label: 'Emirates ID Expiry',
      type: 'DATE',
      required: false,
      category: 'CUSTOMER_INFO',
      order: 19
    },
    'birthPlace': {
      id: 'birth-place',
      name: 'Birth Place',
      label: 'Birth Place',
      type: 'TEXT',
      required: false,
      category: 'CUSTOMER_INFO',
      order: 20
    },
    'birthCertificate': {
      id: 'birth-certificate',
      name: 'Birth Certificate',
      label: 'Birth Certificate Number',
      type: 'TEXT',  // Changed to match the type in API response
      required: false,
      category: 'CUSTOMER_INFO',
      order: 21
    },
    
    // Additional fields from the workflow definition
    'uid': {
      id: 'uid',
      name: 'UID',
      label: 'Customer UID',
      type: 'TEXT',
      required: true,
      category: 'CUSTOMER_INFO',
      order: 22
    },
    'customerNumber': {
      id: 'customer-number',
      name: 'Customer Number',
      label: 'Customer Number',
      type: 'TEXT',
      required: false,
      category: 'CUSTOMER_INFO',
      order: 23
    },
    'customerName': {
      id: 'customer-name',
      name: 'Customer Name',
      label: 'Customer Name',
      type: 'TEXT',
      required: true,
      category: 'CUSTOMER_INFO',
      order: 24
    },
    'customerType': {
      id: 'customer-type',
      name: 'Customer Type',
      label: 'Customer Type',
      type: 'TEXT',
      required: false,
      category: 'CUSTOMER_INFO',
      order: 25
    }
  };
  
  // If we have a mapping for this field code, return it
  if (fieldMappings[fieldCode]) {
    console.log(`Found mapping for field code: ${fieldCode}`);
    return fieldMappings[fieldCode];
  }
  
  // For unknown fields, create a generic field with the code as the name
  console.log(`No mapping found for field code: ${fieldCode}, using default mapping`);
  return {
    id: `field-${fieldCode}`,
    name: fieldCode,
    label: fieldCode.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
    type: 'TEXT',  // Default to TEXT to match API response
    required: false,
    category: 'CUSTOMER_INFO',
    order: 100
  };
};

/**
 * Get fields for workflow steps based on service IDs
 * @param {Array} steps - Workflow steps with service IDs
 * @returns {Promise<Object>} Object containing customer info fields and form fields
 */
export const getFieldsForWorkflowSteps = async (steps) => {
  try {
    if (!steps || !Array.isArray(steps) || steps.length === 0) {
      console.warn('No steps provided to getFieldsForWorkflowSteps');
      return { customerInfoFields: [], formFields: [] };
    }
    
    console.log(`Getting fields for ${steps.length} workflow steps`);
    
    // Extract service IDs from steps
    const serviceIds = steps
      .filter(step => step.serviceId)
      .map(step => step.serviceId);
    
    if (serviceIds.length === 0) {
      console.warn('No service IDs found in workflow steps');
      return { customerInfoFields: [], formFields: [] };
    }
    
    console.log(`Found ${serviceIds.length} service IDs in workflow steps`);
    
    // Get all services with their fields
    const servicesWithFields = await getAllServicesWithFields();
    
    if (!servicesWithFields || servicesWithFields.length === 0) {
      console.warn('No services with fields found');
      return { customerInfoFields: [], formFields: [] };
    }
    
    console.log(`Retrieved ${servicesWithFields.length} services with fields`);
    
    // Filter services by service IDs in workflow steps
    const relevantServices = servicesWithFields.filter(service => 
      serviceIds.includes(service.id) || 
      serviceIds.some(id => id.toString() === service.id.toString())
    );
    
    console.log(`Found ${relevantServices.length} relevant services for this workflow`);
    
    // Extract fields from relevant services
    const customerInfoFields = [];
    const formFields = [];
    
    // First, process the static fields from the workflow steps themselves
    let staticFieldsProcessed = false;
    
    // Helper function to convert non-array fields to arrays if needed
    const ensureArray = (field) => {
      if (!field) return [];
      return Array.isArray(field) ? field : [field];
    };
    
    // Process each step
    steps.forEach((step, index) => {
      // Process each step
      
      // Handle case where staticFields might be a string or object instead of array
      if (typeof step.staticFields === 'string') {
        try {
          step.staticFields = JSON.parse(step.staticFields);
          // Successfully parsed staticFields
        } catch (error) {
          // Error occurred while parsing staticFields
        }
      }
      
      // Now process the static fields
      const staticFields = ensureArray(step.staticFields);
      
      if (staticFields.length > 0) {
        // Found static fields in the step
        staticFieldsProcessed = true;
        
        staticFields.forEach(staticField => {
          // Handle different possible field formats
          const fieldCode = staticField.code || staticField.name || staticField;
          if (!fieldCode) {
            return; // Skip fields with no code or name
          }
          
          // Process this static field
          // Map static field code to a proper field object
          const mappedField = mapStaticFieldCodeToField(fieldCode);
          
          if (mappedField) {
            // Create field with service and step information
            const fieldWithService = {
              ...mappedField,
              serviceId: step.serviceId,
              serviceName: step.serviceName || step.name,
              stepId: step.id,
              category: 'CUSTOMER_INFO', // Static fields are always customer info
              required: staticField.required !== undefined ? staticField.required : mappedField.required
            };
            
            // Only add if not already present (avoid duplicates)
            if (!customerInfoFields.some(f => f.id === mappedField.id)) {
              // Add this field to customerInfoFields
              customerInfoFields.push(fieldWithService);
            } else {
              // Skip duplicate field
            }
          } else {
            // Could not map this static field
          }
        });
      } else {
        // No static fields in this step
      }
    });
    
    // If we still don't have any static fields, try to create default ones based on field properties
    if (customerInfoFields.length === 0) {
      // Try to infer customer info fields from regular fields
      steps.forEach(step => {
        // Check fields array for customer info fields or fields with common static field codes
        const fields = ensureArray(step.fields);
        fields.forEach(field => {
          // Check if this field should be a static/customer info field
          if (field.category === 'CUSTOMER_INFO' || 
              (field.code && ['firstName', 'lastName', 'email', 'phone', 'address', 'gender', 'dob'].some(pattern => 
                field.code.toLowerCase().includes(pattern.toLowerCase())
              ))) {
            // Found a field that should be treated as a customer info field
            const mappedField = mapStaticFieldCodeToField(field.code || field.name);
            if (mappedField && !customerInfoFields.some(f => f.id === mappedField.id)) {
              const fieldWithService = {
                ...mappedField,
                serviceId: step.serviceId,
                serviceName: step.serviceName || step.name,
                stepId: step.id,
                category: 'CUSTOMER_INFO',
                required: field.required !== undefined ? field.required : mappedField.required
              };
              customerInfoFields.push(fieldWithService);
              staticFieldsProcessed = true;
            }
          }
        });
      });
    }
    
    // Then process regular fields as before
    relevantServices.forEach(service => {
      if (service.fields && Array.isArray(service.fields)) {
        service.fields.forEach(field => {
          // Create a copy of the field with service information
          const fieldWithService = {
            ...field,
            serviceId: service.id,
            serviceName: service.name
          };
          
          // Categorize fields based on their category
          if (field.category === 'CUSTOMER_INFO') {
            // Only add if not already present (avoid duplicates)
            if (!customerInfoFields.some(f => f.id === field.id)) {
              customerInfoFields.push(fieldWithService);
            }
          } else {
            formFields.push(fieldWithService);
          }
        });
      }
    });
    
    // Log whether we found static fields
    if (staticFieldsProcessed) {
      console.log(`Using ${customerInfoFields.length} static fields from workflow definition`);
    } else {
      console.log('No static fields found in workflow definition');
    }
    
    // Note: Removed the default field fallback to enforce using static fields
    
    console.log(`Extracted ${customerInfoFields.length} customer info fields and ${formFields.length} form fields`);
    
    return { customerInfoFields, formFields };
  } catch (error) {
    console.error(`Error in getFieldsForWorkflowSteps: ${error.message}`);
    return { customerInfoFields: [], formFields: [] };
  }
};

/**
 * Delete a workflow
 * @param {string} id - Workflow ID
 * @param {boolean} force - Whether to force delete even if there are active instances
 * @returns {Promise<Object>} Response data
 */
export const deleteWorkflow = async (id, force = false) => {
  try {
    console.log(`Deleting workflow with ID: ${id}, force: ${force}`);
    return await api.delete(`${BASE_URL}/${id}?force=${force}`);
  } catch (error) {
    console.error(`Error deleting workflow ${id}:`, error);
    throw error;
  }
};

/**
 * Toggle the active status of a workflow (activate or deactivate)
 * @param {string} id - Workflow ID
 * @param {boolean} activate - If true, activate; if false, deactivate
 * @returns {Promise<Object>} Updated workflow data
 */
export const toggleWorkflowStatus = async (id, activate) => {
  try {
    console.log(`Toggling status for workflow with ID: ${id}, activate: ${activate}`);
    
    // Try both authenticated and public endpoints
    let response;
    let error;
    
    // First try the authenticated endpoint
    try {
      console.log('Trying authenticated endpoint');
      const authEndpoint = activate
        ? `${BASE_URL}/${id}/activate`
        : `${BASE_URL}/${id}/deactivate`;
      response = await api.put(authEndpoint);
      console.log('Authenticated endpoint succeeded:', response);
      return response;
    } catch (authError) {
      console.warn('Authenticated endpoint failed:', authError);
      error = authError;
    }
    
    // If authenticated endpoint failed, try public endpoint
    try {
      console.log('Trying public endpoint');
      const publicEndpoint = activate
        ? `/v1/api/public/workflows/${id}/activate`
        : `/v1/api/public/workflows/${id}/deactivate`;
      response = await api.put(publicEndpoint);
      console.log('Public endpoint succeeded:', response);
      return response;
    } catch (publicError) {
      console.warn('Public endpoint failed:', publicError);
      // If both endpoints failed, throw the original error
      throw error || publicError;
    }
  } catch (error) {
    console.error(`Error toggling workflow status ${id}:`, error);
    throw error;
  }
};

/**
 * Generate a multi-service workflow template
 * @param {Object} templateRequest - The template request containing service configurations, execution modes, and dependencies
 * @returns {Promise<Object>} Generated workflow template
 */
export async function generateMultiServiceWorkflowTemplate(templateRequest) {
  try {
    console.log('Generating multi-service workflow template with request:', templateRequest);
    
    // Ensure we're using the correct API endpoint with proper auth
    const authToken = localStorage.getItem('jwt_token');
    if (!authToken) {
      throw new Error('Authentication token not available. Please log in again.');
    }
    
    // Log token information (first 20 chars only for security)
    console.log('Using token:', authToken.substring(0, 20) + '...');
    
    // Make direct API call to ensure we're hitting the correct endpoint
    const response = await fetch(`${API_BASE_URL}/api/workflow-templates/generate-multi-service`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify(templateRequest)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`HTTP error! Status: ${response.status}, Error: ${errorText}`);
      throw new Error(`HTTP error! Status: ${response.status}, Error: ${errorText}`);
    }
    
    const responseData = await response.json();
    console.log('Successfully generated multi-service workflow template:', responseData);
    
    // Validate that the response has the expected structure
    if (!responseData.definition || !responseData.definition.steps) {
      console.warn('Response has unexpected format:', responseData);
    } else {
      console.log('Response has correct structure with', 
                 responseData.definition.steps.length, 'steps');
    }
    
    return responseData;
  } catch (error) {
    console.error('Error in generateMultiServiceWorkflowTemplate:', error);
    throw error;
  }
};

/**
 * Start a workflow instance
 * @param {string} instanceId - The ID of the workflow instance to start
 * @returns {Promise<Object>} The started workflow instance
 */
export const startWorkflowInstance = async (instanceId) => {
  try {
    console.log(`Starting workflow instance: ${instanceId}`);
    
    // Use the api service which handles authentication automatically
    const response = await api.post(`/api/workflow-instances/${instanceId}/start`);
    console.log('Workflow instance started successfully:', response);
    return response;
  } catch (error) {
    console.error('Error starting workflow instance:', error);
    
    // Provide detailed error information
    const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
    const errorStatus = error.response?.status || 'UNKNOWN';
    const errorDetails = {
      message: `Failed to start workflow instance: ${errorMessage}`,
      status: errorStatus,
      originalError: error
    };
    
    // Log detailed error information for debugging
    console.error('Detailed error:', errorDetails);
    
    throw errorDetails;
  }
};

/**
 * Create a workflow request
 * @param {Object} requestData - The workflow request data
 * @returns {Promise<Object>} The created workflow request
 */
const createWorkflowRequest = async (requestData) => {
  try {
    console.log('Creating workflow request with structured fields');
    
    // Extract workflow definition and check if it has proper field structure
    const { workflowId, workflow } = requestData;
    let definition = null;
    
    // Try to get workflow definition from the request if available
    if (workflow && workflow.definition) {
      definition = typeof workflow.definition === 'string' 
        ? JSON.parse(workflow.definition) 
        : workflow.definition;
    }
    
    // Get the workflow definition to determine which steps and fields exist
    let workflowDefinition = null;
    try {
      const workflowResponse = await getWorkflowById(requestData.workflowId);
      if (workflowResponse && workflowResponse.data) {
        workflowDefinition = workflowResponse.data;
        // Parse the definition if it's a string
        if (workflowDefinition.definition && typeof workflowDefinition.definition === 'string') {
          workflowDefinition.parsedDefinition = JSON.parse(workflowDefinition.definition);
        }
      }
    } catch (error) {
      console.error('Failed to fetch workflow definition', error);
      // Continue without the definition, we'll do our best
    }
    
    // Initialize the request with core metadata
    const structuredRequest = {
      workflowId: requestData.workflowId,
      customerOrganisationId: requestData.customerOrganisationId,
      title: requestData.requestTitle,
      description: requestData.description,
      customerUid: requestData.customerUid || '',
      
      // Global metadata
      metadata: {
        customerUid: requestData.customerUid || '',
        requestTitle: requestData.requestTitle || '',
        description: requestData.description || ''
      },
      
      // Initialize empty steps array
      steps: []
    };
    
    // Flag for file uploads
    let hasFileUploads = false;
    
    // Process fields by step using the workflow definition
    if (workflowDefinition && workflowDefinition.parsedDefinition && 
        workflowDefinition.parsedDefinition.steps && 
        Array.isArray(workflowDefinition.parsedDefinition.steps)) {
      
      // Iterate through each step in the workflow definition
      workflowDefinition.parsedDefinition.steps.forEach(stepDef => {
        // Create a new step with field values
        const step = {
          id: stepDef.id,
          name: stepDef.name,
          serviceId: stepDef.serviceId,
          serviceName: stepDef.serviceName,
          executionMode: stepDef.executionMode || 'SEQUENTIAL',
          required: stepDef.required || false,
          status: 'PENDING',
          completionStatus: 'NOT_STARTED',
          fieldValues: {
            fields: {},
            staticFields: {}
          }
        };
        
        // Extract field definitions for this step
        const stepRegularFields = stepDef.fields || [];
        const stepStaticFields = stepDef.staticFields || [];
        
        // Map fields from requestData to their proper step and category
        if (requestData.customerFields) {
          Object.entries(requestData.customerFields).forEach(([fieldName, fieldValue]) => {
            // Check if this is a regular field in this step
            const regularField = stepRegularFields.find(
              f => f.label === fieldName || f.code === fieldName
            );
            
            // Check if this is a static field in this step
            const staticField = stepStaticFields.find(
              f => f.code === fieldName
            );
            
            // Add to the appropriate field category
            if (regularField) {
              const fieldCode = regularField.code;
              
              // Check if this is a file field
              if (regularField.type === 'PDF' || regularField.type === 'FILE' || regularField.type === 'IMAGE') {
                step.fieldValues.fields[fieldCode] = processFileForUpload(fieldValue, fieldName);
                hasFileUploads = true;
              } else {
                step.fieldValues.fields[fieldCode] = fieldValue;
              }
            } else if (staticField) {
              // Check if this might be a file field
              if (staticField.type === 'PDF' || staticField.type === 'FILE' || staticField.type === 'IMAGE' ||
                  fieldName.toLowerCase().includes('certificate')) {
                step.fieldValues.staticFields[fieldName] = processFileForUpload(fieldValue, fieldName);
                hasFileUploads = true;
              } else {
                step.fieldValues.staticFields[fieldName] = fieldValue;
              }
            }
          });
        }
        
        // Check if any fields were populated for this step
        if (Object.keys(step.fieldValues.fields).length > 0 || 
            Object.keys(step.fieldValues.staticFields).length > 0) {
          // Add the step to the request only if it has fields
          structuredRequest.steps.push(step);
        }
      });
    } else {
      // Fallback if we couldn't get the workflow definition
      // Create a generic step with all fields
      const genericStep = {
        id: 'step-generic',
        name: 'Generic Step',
        status: 'PENDING',
        completionStatus: 'NOT_STARTED',
        executionMode: 'SEQUENTIAL',
        required: true,
        fieldValues: {
          fields: {},
          staticFields: {}
        }
      };
      
      // Process each field and add to the generic step
      if (requestData.customerFields) {
        Object.entries(requestData.customerFields).forEach(([fieldName, fieldValue]) => {
          // Check if field name might be a file
          const possibleFileField = fieldName.toLowerCase().includes('file') || 
                                   fieldName.toLowerCase().includes('document') ||
                                   fieldName.toLowerCase().includes('certificate') ||
                                   fieldName.toLowerCase().includes('upload') ||
                                   (typeof fieldValue === 'string' && fieldValue.includes('target'));
          
          if (possibleFileField) {
            // Treat as file
            genericStep.fieldValues.staticFields[fieldName] = processFileForUpload(fieldValue, fieldName);
            hasFileUploads = true;
          } else if (fieldName.startsWith('field_')) {
            // Likely a regular field with code
            genericStep.fieldValues.fields[fieldName] = fieldValue;
          } else {
            // Default to static fields
            genericStep.fieldValues.staticFields[fieldName] = fieldValue;
          }
        });
      }
      
      // Add the generic step
      structuredRequest.steps.push(genericStep);
    }
    
    // Set hasFileUploads flag
    structuredRequest.hasFileUploads = hasFileUploads;
    
    // Send the request with proper authentication
    const response = await api.post('/api/workflow-requests', structuredRequest);
    return response.data;
  } catch (error) {
    console.error('Error creating workflow request:', error);
    throw error;
  }
};

/**
 * Process a file field object into a proper format for S3 upload
 * @param {Object} fileObj - The file object from the form
 * @param {string} fieldName - The name of the field
 * @returns {Object} Formatted file object ready for S3 upload
 */
const processFileForUpload = (fileObj, fieldName) => {
  // Check if we have a proper file object with target property (from form input)
  if (fileObj && typeof fileObj === 'object') {
    // Handle stringified objects
    if (typeof fileObj === 'string' && fileObj.includes('target')) {
      try {
        fileObj = JSON.parse(fileObj);
      } catch (e) {
        console.error('Failed to parse file object string:', e);
      }
    }
    
    // Extract file data from various possible formats
    let file = null;
    if (fileObj.target && fileObj.target.files && fileObj.target.files.length) {
      // From a file input element
      file = fileObj.target.files[0];
    } else if (fileObj.file) {
      // Directly provided file object
      file = fileObj.file;
    } else if (fileObj instanceof File) {
      // Direct File object
      file = fileObj;
    }
    
    if (file) {
      // Create S3-compatible file object
      return {
        fileName: file.name || fieldName,
        fileType: file.type || 'application/octet-stream',
        content: file,
        // Include file size for validation
        fileSize: file.size,
        // Set upload target to S3
        storageType: 's3',
        // S3 bucket from application.properties
        bucketName: 'thaklis-s3-east1'
      };
    }
  }
  
  // Fallback for unparseable file objects - create stub
  return {
    fileName: fieldName,
    fileType: 'application/octet-stream',
    content: null,
    storageType: 's3',
    bucketName: 'thaklis-s3-east1'
  };
};

export default {
  getWorkflowById,
  getAllWorkflows,
  getWorkflowAssignments,
  createWorkflowRequest,
  getCustomerWorkflowAssignments,
  getOrganizationWorkflowAssignments,
  getCustomerOrganizations,
  assignWorkflow,
  deactivateAssignment,
  createWorkflow,
  updateWorkflow,
  testDirectFetch,
  testDirectFetchWithFetch,
  getWorkflowFields,
  getAllServicesWithFields,
  getFieldsForWorkflowSteps,
  startWorkflowInstance,
  deleteWorkflow,
  toggleWorkflowStatus,
  generateMultiServiceWorkflowTemplate,
  startWorkflowInstance
};
